import React, { useState, useEffect, useCallback } from 'react';
import { Save, RotateCcw } from 'lucide-react';
import { programsAdminAPI } from '../../services/api';

const TEMPLATE_LABELS = {
  welcome: 'Boas-vindas (cadastro)',
  passwordReset: 'Redefinição de senha',
  achievementUnlocked: 'Conquista desbloqueada',
};

const VARS_HINT = {
  welcome: '{{name}}, {{appName}}, {{primaryColor}}, {{logoUrl}}',
  passwordReset: '{{name}}, {{resetLink}}, {{appName}}, {{primaryColor}}',
  achievementUnlocked: '{{name}}, {{achievement}}, {{appName}}, {{primaryColor}}',
};

const ProgramEmailTemplates = ({ programId }) => {
  const [templates, setTemplates] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [draft, setDraft] = useState({ subject: '', html: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await programsAdminAPI.listEmailTemplates(programId);
      const list = res.data?.data || [];
      setTemplates(list);
      if (list.length && !activeKey) {
        setActiveKey(list[0].key);
        setDraft({ subject: list[0].subject, html: list[0].html });
      }
    } catch (err) {
      console.error('Erro ao carregar templates:', err);
    } finally {
      setLoading(false);
    }
  }, [programId, activeKey]);

  useEffect(() => {
    load();
  }, [load]);

  const selectTemplate = (key) => {
    const t = templates.find((x) => x.key === key);
    if (!t) return;
    setActiveKey(key);
    setDraft({ subject: t.subject, html: t.html });
    setFeedback(null);
  };

  const save = async () => {
    if (!activeKey) return;
    setSaving(true);
    setFeedback(null);
    try {
      await programsAdminAPI.upsertEmailTemplate(programId, activeKey, draft);
      setFeedback({ type: 'success', message: 'Template salvo.' });
      await load();
    } catch (err) {
      setFeedback({
        type: 'error',
        message: err.response?.data?.message || 'Falha ao salvar template.',
      });
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = async () => {
    if (!activeKey) return;
    if (!window.confirm('Remover override e voltar ao template padrão?')) return;
    setSaving(true);
    try {
      await programsAdminAPI.deleteEmailTemplate(programId, activeKey);
      setFeedback({ type: 'success', message: 'Override removido.' });
      await load();
    } catch (err) {
      setFeedback({
        type: 'error',
        message: err.response?.data?.message || 'Falha ao remover.',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ padding: 16 }}>Carregando templates...</div>;
  }

  const active = templates.find((t) => t.key === activeKey);

  return (
    <section className="form-section">
      <h2>Templates de Email</h2>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        {templates.map((t) => (
          <button
            key={t.key}
            type="button"
            className={'btn ' + (activeKey === t.key ? 'btn-primary' : 'btn-ghost')}
            onClick={() => selectTemplate(t.key)}
          >
            {TEMPLATE_LABELS[t.key] || t.key}
            {t.source === 'program' && (
              <span style={{ marginLeft: 6, fontSize: 11, opacity: 0.8 }}>(custom)</span>
            )}
          </button>
        ))}
      </div>

      {active && (
        <>
          <div style={{ marginBottom: 12, fontSize: 12, opacity: 0.7 }}>
            Variáveis disponíveis: <code>{VARS_HINT[activeKey] || ''}</code>
          </div>
          <div className="form-grid">
            <label className="span-2">
              <span>Subject</span>
              <input
                type="text"
                value={draft.subject}
                onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
              />
            </label>
            <label className="span-2">
              <span>HTML</span>
              <textarea
                rows={14}
                value={draft.html}
                onChange={(e) => setDraft({ ...draft, html: e.target.value })}
                style={{ fontFamily: 'monospace', fontSize: 12 }}
              />
            </label>
          </div>

          {feedback && (
            <div
              className={'alert ' + (feedback.type === 'error' ? 'alert-danger' : '')}
              style={{ marginTop: 12 }}
            >
              {feedback.message}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button
              type="button"
              className="btn btn-primary btn-with-icon"
              onClick={save}
              disabled={saving}
            >
              <Save size={16} /> {saving ? 'Salvando...' : 'Salvar override'}
            </button>
            {active.source === 'program' && (
              <button
                type="button"
                className="btn btn-ghost btn-with-icon"
                onClick={resetToDefault}
                disabled={saving}
              >
                <RotateCcw size={16} /> Voltar ao padrão
              </button>
            )}
          </div>
        </>
      )}
    </section>
  );
};

export default ProgramEmailTemplates;
